import React, { useMemo } from 'react';
import type { GuidanceTreeNode } from '../personalLayer/types';
import { layoutGuidanceNetwork, networkEdgePath } from './guidanceNetworkLayout';
import styles from '../ControlBridge.module.css';

export interface GuidanceNetworkViewProps {
  root: GuidanceTreeNode;
  focusActivityId?: string | null;
  onSelectActivity?: (activityId: string) => void;
}

export const GuidanceNetworkView: React.FC<GuidanceNetworkViewProps> = ({
  root,
  focusActivityId,
  onSelectActivity,
}) => {
  const layout = useMemo(() => layoutGuidanceNetwork(root), [root]);

  return (
    <div className={styles.guidanceNetworkWrap} data-testid="guidance-network-view">
      {layout.nodes.length === 0 ? (
        <div className={styles.guidanceNetworkEmpty} role="status">
          No activity dependencies to display in this scope.
        </div>
      ) : (
      <div
        className={styles.guidanceNetworkCanvas}
        style={{ width: layout.width, height: layout.height }}
        role="img"
        aria-label="Activity dependency network"
      >
        <svg
          className={styles.guidanceNetworkSvg}
          width={layout.width}
          height={layout.height}
          aria-hidden
        >
          <defs>
            <marker id="guidanceNetArrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
              <path d="M0 0 L5.5 3 L0 6 Z" fill="#c2cad6" />
            </marker>
            <marker id="guidanceNetArrowCrit" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
              <path d="M0 0 L5.5 3 L0 6 Z" fill="#ef4444" />
            </marker>
          </defs>
          {layout.edges.map((edge) => {
            const from = layout.nodes.find((n) => n.id === edge.from);
            const to = layout.nodes.find((n) => n.id === edge.to);
            if (!from || !to) return null;
            return (
              <path
                key={edge.id}
                d={networkEdgePath(from, to)}
                fill="none"
                stroke={edge.critical ? '#ef4444' : '#c2cad6'}
                strokeWidth={edge.critical ? 2 : 1.5}
                markerEnd={`url(#${edge.critical ? 'guidanceNetArrowCrit' : 'guidanceNetArrow'})`}
              />
            );
          })}
        </svg>
        {layout.nodes.map((node) => (
          <button
            key={node.id}
            type="button"
            className={[
              styles.guidanceNetworkNode,
              node.critical ? styles.guidanceNetworkNodeCritical : '',
              focusActivityId === node.id ? styles.guidanceNetworkNodeFocus : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ left: node.x, top: node.y, width: node.w, minHeight: node.h }}
            aria-pressed={focusActivityId === node.id}
            onClick={() => onSelectActivity?.(node.id)}
          >
            <span className={styles.guidanceNetworkNodeLabel}>{node.label}</span>
          </button>
        ))}
      </div>
      )}
    </div>
  );
};
